class StepContactForm {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 5;
    this.formData = {};
    this.uploadedFiles = {
      prescription: [],
      photos: []
    };
    this.storageKey = 'stepContactForm_data';
    this.filesStorageKey = 'stepContactForm_files';
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupFileInputs();
    this.setupValidation();
    this.setupConditionalFields();
    this.setupLocalStorage();
    this.loadCustomerData();
    this.restoreFromLocalStorage();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-action="next"]')) {
        e.preventDefault();
        e.stopPropagation();
        const step = parseInt(e.target.getAttribute('data-step'));
        this.nextStep(step);
      }
      
      if (e.target.matches('[data-action="prev"]')) {
        e.preventDefault();
        e.stopPropagation();
        const step = parseInt(e.target.getAttribute('data-step'));
        this.prevStep(step);
      }

      // File removal
      if (e.target.matches('.file-preview__remove')) {
        e.preventDefault();
        const fileId = e.target.getAttribute('data-file-id');
        const fileType = e.target.getAttribute('data-file-type');
        this.removeFile(fileType, fileId);
      }
    });

    const form = document.querySelector('.step-contact-form__main-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        this.handleSubmit(e);
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault();
        this.nextStep(this.currentStep);
      }
    });
  }

  setupLocalStorage() {
    // Save data on any input change
    document.addEventListener('input', (e) => {
      if (e.target.matches('input, textarea, select')) {
        this.saveToLocalStorage();
      }
    });

    // Save data on checkbox change
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"], select')) {
        this.saveToLocalStorage();
      }
    });
  }

  saveToLocalStorage() {
    try {
      const formData = this.collectFormData();
      localStorage.setItem(this.storageKey, JSON.stringify(formData));
      
      // Save file information (names and sizes, not actual files)
      const fileInfo = {
        prescription: this.uploadedFiles.prescription.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type
        })),
        photos: this.uploadedFiles.photos.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type
        }))
      };
      localStorage.setItem(this.filesStorageKey, JSON.stringify(fileInfo));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  restoreFromLocalStorage() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (savedData) {
        const formData = JSON.parse(savedData);
        this.populateFormFields(formData);
      }

      // Note: File restoration is limited due to security restrictions
      // We can only show that files were previously selected
      const savedFiles = localStorage.getItem(this.filesStorageKey);
      if (savedFiles) {
        const fileInfo = JSON.parse(savedFiles);
        this.showPreviousFileInfo(fileInfo);
      }
    } catch (error) {
      console.warn('Failed to restore from localStorage:', error);
    }
  }

  collectFormData() {
    const formData = {};
    const form = document.querySelector('.step-contact-form__main-form');
    const fields = form.querySelectorAll('input, textarea, select');
    
    fields.forEach(field => {
      if (field.name && field.type !== 'file') {
        if (field.type === 'checkbox') {
          formData[field.name] = field.checked;
        } else {
          formData[field.name] = field.value;
        }
      }
    });
    
    return formData;
  }

  populateFormFields(formData) {
    const form = document.querySelector('.step-contact-form__main-form');
    const fields = form.querySelectorAll('input, textarea, select');
    
    fields.forEach(field => {
      if (field.name && formData[field.name] !== undefined && field.type !== 'file') {
        if (field.type === 'checkbox') {
          field.checked = formData[field.name];
        } else {
          field.value = formData[field.name];
        }
      }
    });

    // Trigger conditional field display
    this.updateConditionalFields();
  }

  showPreviousFileInfo(fileInfo) {
    if (fileInfo.prescription && fileInfo.prescription.length > 0) {
      const prescriptionInput = document.getElementById(document.querySelector('.step-contact-form__main-form').id + '-prescription');
      if (prescriptionInput) {
        const label = prescriptionInput.parentElement.querySelector('.step-form__file-label');
        const totalSize = fileInfo.prescription.reduce((sum, f) => sum + f.size, 0);
        const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
        label.innerHTML = `
          <div style="text-align: center;">
            <div style="font-weight: 500; color: #6b7280; margin-bottom: 0.25rem;">
              📁 ${fileInfo.prescription.length} Datei(en) zuvor ausgewählt
            </div>
            <div style="font-size: 0.75rem; color: #6b7280;">
              Gesamt: ${sizeInMB} MB (Dateien müssen erneut ausgewählt werden)
            </div>
          </div>
        `;
      }
    }

    if (fileInfo.photos && fileInfo.photos.length > 0) {
      const photosInput = document.getElementById(document.querySelector('.step-contact-form__main-form').id + '-photos');
      if (photosInput) {
        const label = photosInput.parentElement.querySelector('.step-form__file-label');
        const totalSize = fileInfo.photos.reduce((sum, f) => sum + f.size, 0);
        const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
        label.innerHTML = `
          <div style="text-align: center;">
            <div style="font-weight: 500; color: #6b7280; margin-bottom: 0.25rem;">
              📁 ${fileInfo.photos.length} Datei(en) zuvor ausgewählt
            </div>
            <div style="font-size: 0.75rem; color: #6b7280;">
              Gesamt: ${sizeInMB} MB (Dateien müssen erneut ausgewählt werden)
            </div>
          </div>
        `;
      }
    }
  }

  loadCustomerData() {
    // Check if customer is logged in (Shopify customer object)
    if (window.customerData || (typeof Shopify !== 'undefined' && Shopify.customer)) {
      const customer = window.customerData || Shopify.customer;
      
      if (customer) {
        // Pre-populate customer fields
        const form = document.querySelector('.step-contact-form__main-form');
        const formId = form.id;
        
        // Fill in customer data if available
        const fields = {
          [`${formId}-first-name`]: customer.first_name || customer.firstName,
          [`${formId}-last-name`]: customer.last_name || customer.lastName,
          [`${formId}-email`]: customer.email,
          [`${formId}-phone`]: customer.phone || customer.default_address?.phone
        };

        // Fill address data if available
        if (customer.default_address || customer.defaultAddress) {
          const address = customer.default_address || customer.defaultAddress;
          fields[`${formId}-street`] = `${address.address1 || ''} ${address.address2 || ''}`.trim();
          fields[`${formId}-city`] = address.city;
          fields[`${formId}-postal-code`] = address.zip;
        }

        // Populate fields
        Object.entries(fields).forEach(([id, value]) => {
          if (value) {
            const field = document.getElementById(id);
            if (field && !field.value) { // Only fill if empty
              field.value = value;
            }
          }
        });
      }
    }
  }

  setupConditionalFields() {
    // Handle consultation type changes
    const consultationSelect = document.querySelector('[name="contact[consultation]"]');
    if (consultationSelect) {
      consultationSelect.addEventListener('change', () => {
        this.updateConditionalFields();
      });
    }
  }

  updateConditionalFields() {
    const consultationSelect = document.querySelector('[name="contact[consultation]"]');
    const textField = document.getElementById('consultation-text-field');
    const callbackField = document.getElementById('callback-reason-field');
    const textInput = document.querySelector('[name="contact[consultation_text]"]');
    const callbackInput = document.querySelector('[name="contact[callback_reason]"]');

    if (consultationSelect && textField && callbackField) {
      const value = consultationSelect.value;
      
      // Hide all conditional fields
      textField.style.display = 'none';
      callbackField.style.display = 'none';
      
      // Remove required attributes
      if (textInput) textInput.removeAttribute('required');
      if (callbackInput) callbackInput.removeAttribute('required');
      
      // Show relevant field based on selection
      if (value === 'free-text') {
        textField.style.display = 'block';
        if (textInput) textInput.setAttribute('required', 'required');
      } else if (value === 'callback') {
        callbackField.style.display = 'block';
        if (callbackInput) callbackInput.setAttribute('required', 'required');
      }
      // For 'appointment', we show the Calendly widget in step 5 (already visible)
    }
  }

  nextStep(step) {
    if (this.validateStep(step)) {
      if (step < this.totalSteps) {
        this.showStep(step + 1);
        this.updateProgress(step + 1);
        this.saveFormData(step);
        this.saveToLocalStorage();
      }
    }
  }

  prevStep(step) {
    if (step > 1) {
      this.showStep(step - 1);
      this.updateProgress(step - 1);
    }
  }

  showStep(step) {
    document.querySelectorAll('.step-form').forEach(form => {
      form.classList.remove('active');
    });
    
    const currentForms = document.querySelectorAll(`[data-step="${step}"]`);
    if (currentForms) {
      currentForms.forEach(currentForm => {
        currentForm.classList.add('active');
        
        this.currentStep = step;
        
        this.restoreFormData(step);
      });
    }
  }

  updateProgress(step) {
    document.querySelectorAll('.step-title__text').forEach((title, index) => {
      const stepNumber = index + 1;
      if (stepNumber === step) {
        title.style.color = '#A35B3C';
      } else {
        title.style.color = '#333';
      }
    });
  }

  validateStep(step) {
    const currentForm = document.querySelector(`[data-step="${step}"]`);
    const requiredFields = currentForm.querySelectorAll('[required]');
    let isValid = true;
    let firstInvalidField = null;

    requiredFields.forEach(field => {
      const fieldContainer = field.closest('.step-form__field');
      const isEmpty = !field.value.trim();
      const isCheckbox = field.type === 'checkbox';
      const isFile = field.type === 'file';
      
      let fieldValid = true;
      let errorMessage = '';
      
      if (isCheckbox) {
        fieldValid = field.checked;
        errorMessage = 'Das ist ein Pflichtfeld';
      } else if (isFile) {
        fieldValid = field.files && field.files.length > 0;
        errorMessage = 'Bitte wähle eine Datei';
      } else {
        fieldValid = !isEmpty;
        errorMessage = 'Das ist ein Pflichtfeld';
      }

      fieldContainer.classList.remove('step-form__field--error', 'step-form__field--success');
      const existingError = fieldContainer.querySelector('.field-error');
      const existingSuccess = fieldContainer.querySelector('.field-success');
      if (existingError) existingError.remove();
      if (existingSuccess) existingSuccess.remove();

      if (!fieldValid) {
        fieldContainer.classList.add('step-form__field--error');
        this.showFieldError(fieldContainer, errorMessage);
        isValid = false;
        
        if (!firstInvalidField) {
          firstInvalidField = field;
        }
      } else {
        fieldContainer.classList.add('step-form__field--success');
        this.showFieldSuccess(fieldContainer, '✓ Valid');
      }
    });

    if (firstInvalidField) {
      firstInvalidField.focus();
      firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return isValid;
  }

  setupFileInputs() {
    const fileInputs = document.querySelectorAll('input[type="file"]');
    
    fileInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.handleFileInput(e);
      });
      
      input.addEventListener('dragover', (e) => {
        e.preventDefault();
        const fileLabel = input.parentElement.querySelector('.step-form__file-label');
        fileLabel.style.borderColor = '#EBB9A3';
        fileLabel.style.background = '#FCF6F3';
      });
      
      input.addEventListener('dragleave', (e) => {
        e.preventDefault();
        const fileLabel = input.parentElement.querySelector('.step-form__file-label');
        fileLabel.style.borderColor = '#d1d5db';
        fileLabel.style.background = '#f9fafb';
      });
      
      input.addEventListener('drop', (e) => {
        e.preventDefault();
        const fileLabel = input.parentElement.querySelector('.step-form__file-label');
        fileLabel.style.borderColor = '#d1d5db';
        fileLabel.style.background = '#f9fafb';
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          input.files = files;
          this.handleFileInput({ target: input });
        }
      });
    });
  }

  handleFileInput(e) {
    const input = e.target;
    const fileLabel = input.parentElement.querySelector('.step-form__file-label');
    const fieldContainer = input.closest('.step-form__field');
    const fileType = input.name.includes('prescription') ? 'prescription' : 'photos';
    const previewContainer = fieldContainer.querySelector('.file-preview');
    
    if (input.files.length > 0) {
      // Store files
      this.uploadedFiles[fileType] = Array.from(input.files);
      
      // Update file label
      const totalSize = Array.from(input.files).reduce((total, file) => total + file.size, 0);
      const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
      
      fileLabel.innerHTML = `
        <div style="text-align: center;">
          <div style="font-weight: 500; color: #A35B3C; margin-bottom: 0.25rem;">
            ✓ ${input.files.length} Datei(en) ausgewählt
          </div>
          <div style="font-size: 0.75rem; color: #6b7280;">
            Gesamt: ${sizeInMB} MB
          </div>
        </div>
      `;
      
      // Show file preview
      this.updateFilePreview(fileType, previewContainer);
      
      fieldContainer.classList.remove('step-form__field--error');
      fieldContainer.classList.add('step-form__field--success');
      this.showFieldSuccess(fieldContainer, '✓ Dateien erfolgreich hochgeladen');
      
      // Save to localStorage
      this.saveToLocalStorage();
    } else {
      // Reset everything
      this.uploadedFiles[fileType] = [];
      fileLabel.innerHTML = `<span>${fileType === 'prescription' ? 'Klicken Sie hier, um das Rezept hochzuladen' : 'Klicken Sie hier, um Fotos hochzuladen'}</span>`;
      previewContainer.classList.remove('has-files');
      previewContainer.innerHTML = '';
      
      if (input.hasAttribute('required')) {
        fieldContainer.classList.remove('step-form__field--success');
        fieldContainer.classList.add('step-form__field--error');
        this.showFieldError(fieldContainer, 'Bitte wähle eine Datei');
      }
    }
  }

  updateFilePreview(fileType, previewContainer) {
    const files = this.uploadedFiles[fileType];
    
    if (files.length === 0) {
      previewContainer.classList.remove('has-files');
      previewContainer.innerHTML = '';
      return;
    }

    previewContainer.classList.add('has-files');
    
    let previewHTML = '';
    let totalSize = 0;

    files.forEach((file, index) => {
      totalSize += file.size;
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      const fileId = `${fileType}_${index}`;
      
      let thumbnail = '';
      if (file.type.startsWith('image/')) {
        const objectURL = URL.createObjectURL(file);
        thumbnail = `<img src="${objectURL}" class="file-preview__thumbnail" alt="${file.name}">`;
      } else {
        thumbnail = `<div class="file-preview__thumbnail">📄</div>`;
      }
      
      previewHTML += `
        <div class="file-preview__item" data-file-id="${fileId}">
          <div class="file-preview__info">
            ${thumbnail}
            <div class="file-preview__details">
              <div class="file-preview__name">${file.name}</div>
              <div class="file-preview__size">${sizeInMB} MB</div>
            </div>
          </div>
          <button type="button" class="file-preview__remove" data-file-id="${fileId}" data-file-type="${fileType}">
            Entfernen
          </button>
        </div>
      `;
    });

    const totalSizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
    previewHTML += `
      <div class="file-preview__total">
        Insgesamt: ${files.length} Datei(en), ${totalSizeInMB} MB
      </div>
    `;

    previewContainer.innerHTML = previewHTML;
  }

  removeFile(fileType, fileId) {
    const [type, indexStr] = fileId.split('_');
    const index = parseInt(indexStr);
    
    if (this.uploadedFiles[fileType] && this.uploadedFiles[fileType][index]) {
      // Remove file from array
      this.uploadedFiles[fileType].splice(index, 1);
      
      // Update the file input
      const input = document.querySelector(`[name="contact[${fileType}]"]`);
      if (input) {
        // Create a new DataTransfer object to update the input files
        const dt = new DataTransfer();
        this.uploadedFiles[fileType].forEach(file => {
          dt.items.add(file);
        });
        input.files = dt.files;
        
        // Trigger change event to update UI
        this.handleFileInput({ target: input });
      }
    }
  }
  
  showFieldError(container, message) {
    const existingError = container.querySelector('.field-error');
    if (existingError) {
      existingError.textContent = message;
    } else {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'field-error';
      errorDiv.textContent = message;
      container.appendChild(errorDiv);
    }
  }
  
  showFieldSuccess(container, message) {
    const existingSuccess = container.querySelector('.field-success');
    if (existingSuccess) {
      existingSuccess.textContent = message;
    } else {
      const successDiv = document.createElement('div');
      successDiv.className = 'field-success';
      successDiv.textContent = message;
      container.appendChild(successDiv);
    }
  }
  
  setupValidation() {
    document.addEventListener('input', (e) => {
      if (e.target.matches('input, textarea, select')) {
        this.validateField(e.target);
      }
    });

    document.addEventListener('blur', (e) => {
      if (e.target.type === 'email') {
        this.validateEmail(e.target);
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('select')) {
        this.validateField(e.target);
        
        // Handle conditional fields
        if (e.target.name === 'contact[consultation]') {
          this.updateConditionalFields();
        }
      }
    });
  }

  validateField(field) {
    const fieldContainer = field.closest('.step-form__field');
    const isEmpty = !field.value.trim();
    const isCheckbox = field.type === 'checkbox';
    const isRequired = field.hasAttribute('required');
    
    fieldContainer.classList.remove('step-form__field--error', 'step-form__field--success');
    const existingError = fieldContainer.querySelector('.field-error');
    const existingSuccess = fieldContainer.querySelector('.field-success');
    if (existingError) existingError.remove();
    if (existingSuccess) existingSuccess.remove();
    
    if (isCheckbox) {
      if (field.checked) {
        fieldContainer.classList.add('step-form__field--success');
        this.showFieldSuccess(fieldContainer, '✓ Ausgewählt');
      } else if (isRequired) {
        fieldContainer.classList.add('step-form__field--error');
        this.showFieldError(fieldContainer, 'Das ist ein Pflichtfeld');
      }
    } else if (!isEmpty) {
      fieldContainer.classList.add('step-form__field--success');
      this.showFieldSuccess(fieldContainer, '✓ Valid');
    } else if (isRequired) {
      fieldContainer.classList.add('step-form__field--error');
      this.showFieldError(fieldContainer, 'Das ist ein Pflichtfeld');
    }
  }

  validateEmail(field) {
    const fieldContainer = field.closest('.step-form__field');
    const email = field.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    fieldContainer.classList.remove('step-form__field--error', 'step-form__field--success');
    const existingError = fieldContainer.querySelector('.field-error');
    const existingSuccess = fieldContainer.querySelector('.field-success');
    if (existingError) existingError.remove();
    if (existingSuccess) existingSuccess.remove();
    
    if (email && !emailRegex.test(email)) {
      fieldContainer.classList.add('step-form__field--error');
      this.showFieldError(fieldContainer, 'Bitte geben Sie eine gültige E-Mail-Adresse ein');
    } else if (email && emailRegex.test(email)) {
      fieldContainer.classList.add('step-form__field--success');
      this.showFieldSuccess(fieldContainer, '✓ Gültige E-Mail');
    }
  }

  saveFormData(step) {
    const currentForm = document.querySelector(`[data-step="${step}"]`);
    const fields = currentForm.querySelectorAll('input, textarea, select');
    
    fields.forEach(field => {
      if (field.name) {
        if (field.type === 'checkbox') {
          this.formData[field.name] = field.checked;
        } else if (field.type === 'file') {
          this.formData[field.name] = field.files;
        } else {
          this.formData[field.name] = field.value;
        }
      }
    });
  }

  restoreFormData(step) {
    const currentForm = document.querySelector(`[data-step="${step}"]`);
    const fields = currentForm.querySelectorAll('input, textarea, select');
    
    fields.forEach(field => {
      if (field.name && this.formData[field.name] !== undefined) {
        if (field.type === 'checkbox') {
          field.checked = this.formData[field.name];
        } else if (field.type === 'file') {
          return;
        } else {
          field.value = this.formData[field.name];
        }
      }
    });

    // Update conditional fields after restoring data
    this.updateConditionalFields();
  }

  handleSubmit(e) {
    let allValid = true;
    
    for (let i = 1; i <= this.totalSteps; i++) {
      if (!this.validateStep(i)) {
        allValid = false;
        this.showStep(i);
        break;
      }
    }
    
    if (!allValid) {
      e.preventDefault();
      return false;
    }

    // Before submit: collect all upload URLs from Upload-Lift containers and populate hidden fields
    try {
      const form = e.target.closest('form') || e.target;
      const getUrls = (root) => {
        const urls = [];
        if (!root) return urls;
        // 1) Links generated by the uploader UI
        root.querySelectorAll('a[href^="http"]').forEach(a => {
          const href = a.getAttribute('href');
          if (href && !urls.includes(href)) urls.push(href);
        });
        // 2) Hidden inputs containing URLs
        root.querySelectorAll('input[type="hidden"]').forEach(inp => {
          const val = (inp.value || '').trim();
          if (/^https?:\/\//i.test(val) && !urls.includes(val)) urls.push(val);
        });
        // 3) Any elements carrying a URL in data attributes (common with uploaders)
        root.querySelectorAll('[data-url], [data-file-url], [data-download-url]').forEach(el => {
          const val = el.getAttribute('data-url') || el.getAttribute('data-file-url') || el.getAttribute('data-download-url');
          if (val && /^https?:\/\//i.test(val) && !urls.includes(val)) urls.push(val);
        });
        return urls;
      };

      const vorneContainer = document.querySelector('.upload-rezept-vorne');
      const hintenContainer = document.querySelector('.upload-rezept-hinten');
      const kopfContainer = document.querySelector('.upload-fotos-kopf');

      const vorneUrls = getUrls(vorneContainer);
      const hintenUrls = getUrls(hintenContainer);
      const kopfUrls = getUrls(kopfContainer);

      const byLine = (arr) => arr.join('\n');
      const allUnique = Array.from(new Set([].concat(vorneUrls, hintenUrls, kopfUrls)));

      // Resolve IDs added in Liquid using the form id
      const formId = form.id || '';
      const vorneField = document.getElementById(`${formId}-rezept-vorne-urls`);
      const hintenField = document.getElementById(`${formId}-rezept-hinten-urls`);
      const kopfField = document.getElementById(`${formId}-fotos-kopf-urls`);
      const allField = document.getElementById(`${formId}-all-uploads-urls`);

      if (vorneField) vorneField.value = byLine(vorneUrls);
      if (hintenField) hintenField.value = byLine(hintenUrls);
      if (kopfField) kopfField.value = byLine(kopfUrls);
      if (allField) allField.value = byLine(allUnique);
    } catch (err) {
      console.warn('Failed to collect upload URLs for contact form email:', err);
    }
    
    const submitBtn = e.target.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.classList.add('loading');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Übertragen...';
    }
    
    // Clear localStorage on successful submission
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.filesStorageKey);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    
    return true;
  }

  goToStep(step) {
    if (step >= 1 && step <= this.totalSteps) {
      this.showStep(step);
      this.updateProgress(step);
    }
  }

  resetForm() {
    this.currentStep = 1;
    this.formData = {};
    this.uploadedFiles = { prescription: [], photos: [] };
    
    document.querySelectorAll('.step-contact-form__main-form input, .step-contact-form__main-form textarea, .step-contact-form__main-form select').forEach(field => {
      const fieldContainer = field.closest('.step-form__field');
      
      if (field.type === 'checkbox') {
        field.checked = false;
      } else if (field.type === 'file') {
        field.value = '';
        const fileLabel = field.parentElement.querySelector('.step-form__file-label');
        if (fileLabel) {
          const fileType = field.name.includes('prescription') ? 'prescription' : 'photos';
          fileLabel.innerHTML = `<span>${fileType === 'prescription' ? 'Klicken Sie hier, um das Rezept hochzuladen' : 'Klicken Sie hier, um Fotos hochzuladen'}</span>`;
        }
        // Clear file previews
        const previewContainer = fieldContainer.querySelector('.file-preview');
        if (previewContainer) {
          previewContainer.classList.remove('has-files');
          previewContainer.innerHTML = '';
        }
      } else {
        field.value = '';
      }
      
      fieldContainer.classList.remove('step-form__field--error', 'step-form__field--success');
      const existingError = fieldContainer.querySelector('.field-error');
      const existingSuccess = fieldContainer.querySelector('.field-success');
      if (existingError) existingError.remove();
      if (existingSuccess) existingSuccess.remove();
    });
    
    // Hide conditional fields
    const textField = document.getElementById('consultation-text-field');
    const callbackField = document.getElementById('callback-reason-field');
    if (textField) textField.style.display = 'none';
    if (callbackField) callbackField.style.display = 'none';
    
    // Clear localStorage
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.filesStorageKey);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    
    this.updateProgress(1);
    this.showStep(1);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  if (document.querySelector('.step-contact-form')) {
    window.stepContactForm = new StepContactForm();
  }
});

// Legacy functions for backward compatibility
function nextStep(step) {
  if (window.stepContactForm) {
    window.stepContactForm.nextStep(step);
  }
}

function prevStep(step) {
  if (window.stepContactForm) {
    window.stepContactForm.prevStep(step);
  }
}